
/* @TEMPLATE_@MSAL: Uncomment to use MSAL for authentication.*/
import {
    BrowserAuthError,
    InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { EventType } from "@azure/msal-browser";
import { loginRequest } from '../authConfig';

const DEBUG = true;

// Helper function to encode URI components while preserving + as %2B
// Standard encodeURIComponent converts + to %20 (space), but we want + to be %2B
function encodeURIComponentPreservePlus(str) {
    return encodeURIComponent(str).replace(/%20/g, '+').replace(/\+/g, '%2B');
}

// Helper function to construct a GET URL with query parameters.
export function toQueryString(obj) {
    if (!obj) return "";

    const parts = [];
    for (const [key, value] of Object.entries(obj)) {
        if (value == null) continue;

        if (Array.isArray(value)) {
            for (const item of value) {
                if (item == null) continue;
                parts.push(`${encodeURIComponentPreservePlus(key)}=${encodeURIComponentPreservePlus(item)}`);
            }
            continue;
        }

        // Use the plus-preserving encoding for all parameters
        parts.push(`${encodeURIComponentPreservePlus(key)}=${encodeURIComponentPreservePlus(value)}`);
    }

    return parts.join("&");
}

// Helper function to construct per-API fetch wrapper, that has unified logging, 
// rudimentary error handling and support for local development.
export function createFetchWrapper({
    // function to create the endpoint URL from the endpoint and parameters
    createEndpointUrl,
    // function to fetch dummy data from local development, from endpoint, fetchUrl and fetchParameters
    localFetch,
    // object with endpoint methods (GET, POST, PUT, DELETE) for each endpoint
    endpointMethods,
    // Optional override for backend base URL (e.g., when some APIs are hosted elsewhere)
    backendUrl
}) {
    return async ({
        // endpoint name
        endpoint,
        // query parameters
        parameters,
        // request body for POST/PUT
        body,
        // additional headers
        headers,
        // msal instance, if using
        msal
    }) => {
        // @TEMPLATE_@MSAL - Uncomment this line to use MSAL for authentication.
        const accessToken = await getAccessToken(msal.instance);
        if (!accessToken) {
            if (DEBUG) console.log(`Api :: skipping fetch, no access token :: ${endpoint}`);
            return null;
        }
        const DEFAULT_HEADERS = {
            "Content-Type": "application/json",
            // @TEMPLATE_@MSAL - Uncomment this line to use MSAL for authentication.
            Authorization: `Bearer ${accessToken}`,
            // @TEMPLATE - Add any additional headers here.
            // "x-api-key": apiKey,
        };
        const resolvedBackendUrl = backendUrl ?? process.env.REACT_APP_BACKEND_URL;

        const fetchUrl = `${resolvedBackendUrl}/${createEndpointUrl({ endpoint, parameters, body })}`;

        let fetchParameters = {
            method: endpointMethods[endpoint],
            headers: {
                ...DEFAULT_HEADERS,
                ...headers
            },
        };

        if (
            fetchParameters.method === 'POST' ||
            fetchParameters.method === 'PUT' ||
            fetchParameters.method === 'PATCH'
        ) {
            fetchParameters.body = JSON.stringify(body);
        }

        if (!headers) {
            headers = {};
        }

        try {
            let response = null;

            if (!resolvedBackendUrl) {
                if (DEBUG) console.log(`Api :: LOCAL fetch :: ${fetchParameters.method} :: ${fetchUrl}`);
                if (DEBUG) console.log(fetchParameters);
                response = await localFetch({ endpoint, fetchUrl, fetchParameters, parameters });
            } else {
                if (DEBUG) console.log(`Api :: REMOTE fetch :: ${fetchParameters.method} :: ${fetchUrl}`);
                if (DEBUG) console.log(fetchParameters);
                response = await fetch(fetchUrl, fetchParameters);
            }

            if (DEBUG) console.log("response", `Api :: Response`);
            if (DEBUG) console.log(response);

            if (!response || !response.ok) {
                const errorText = response ? await response.text().catch(() => '') : ''; // fallback if not text
                const status = response?.status ?? 'Unknown';
                if (status === 401) {
                    // Token was rejected by the backend — force a clean re-login
                    await startLoginRedirect(msal.instance);
                    return null;
                }
                const error = new Error(`Api :: Error response (${status}) from fetch: ${fetchUrl} :: ${errorText}`);
                error.status = response?.status;
                throw error;
            }

            const result = await response.json();

            if (DEBUG) console.log(`Api :: fetch :: ${fetchParameters.method} :: ${fetchUrl} :: SUCCESS`);
            if (DEBUG) console.log(result);

            return result;
        } catch (error) {
            if (DEBUG) console.log(`Api :: fetch :: ${fetchParameters.method} :: ${fetchUrl} :: ERROR`);
            console.error('API request error:', error);
            throw error;
        }
    };
}


let msalBootstrapPromise = null;

function setActiveAccountIfNeeded(msalInstance) {
    const accounts = msalInstance.getAllAccounts();
    if (!msalInstance.getActiveAccount() && accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
    }
}

async function startLoginRedirect(msalInstance) {
    if (msalInstance.getActiveAccount()) {
        return;
    }

    try {
        await msalInstance.loginRedirect(loginRequest);
    } catch (error) {
        if (error instanceof BrowserAuthError && error.errorCode === 'interaction_in_progress') {
            if (DEBUG) console.log('Login redirect already in progress');
            return;
        }
        throw error;
    }
}

export function initializeMsal(msalInstance) {
    if (msalBootstrapPromise) {
        return msalBootstrapPromise;
    }

    msalBootstrapPromise = (async () => {
        await msalInstance.initialize();

        setActiveAccountIfNeeded(msalInstance);

        msalInstance.addEventCallback((event) => {
            if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
                const payload = event.payload;
                const account = payload.account;
                msalInstance.setActiveAccount(account);
            }

            if (event.eventType === EventType.LOGIN_FAILURE) {
                console.log("login failure");

                if (event.error && event.error.errorMessage) {
                    console.log(event.error.errorMessage)
                }
            }
        });

        const response = await msalInstance.handleRedirectPromise();
        if (response?.account) {
            msalInstance.setActiveAccount(response.account);
            if (DEBUG) console.log('Login successful:', response.account);
        } else {
            setActiveAccountIfNeeded(msalInstance);
            if (msalInstance.getActiveAccount() && DEBUG) {
                console.log('Already logged in:', msalInstance.getActiveAccount());
            }
        }

        if (!msalInstance.getActiveAccount()) {
            await startLoginRedirect(msalInstance);
            return false;
        }

        return true;
    })();

    return msalBootstrapPromise;
}

export async function getAccessToken(
    msalInstance,
    scopes = loginRequest.scopes,
) {
    const account = msalInstance.getActiveAccount();

    if (!account) {
        console.log('No active account found');
        return null;
    }

    try {
        const silentResult = await msalInstance.acquireTokenSilent({
            scopes: scopes,
            account: account,
        });
        return silentResult.accessToken;
    } catch (error) {
        console.error(error);
        if (error instanceof InteractionRequiredAuthError) {
            // Redirect works reliably on mobile; popups are blocked in mobile Safari.
            try {
                await msalInstance.acquireTokenRedirect({
                    scopes: scopes,
                    account: account,
                });
            } catch (redirectError) {
                if (
                    redirectError instanceof BrowserAuthError
                    && redirectError.errorCode === 'interaction_in_progress'
                ) {
                    if (DEBUG) console.log('Token redirect already in progress');
                    return null;
                }
                throw redirectError;
            }
            return null;
        }

        await startLoginRedirect(msalInstance);
        return null;
    }
}